import { EngagementTracking } from "services";

interface UserPreferenceOptions {
  userId?: string | null;
  userVector?: any;
  itemId?: string | null;
  tags?: string[];
  storage?: {
    itemsKey?: string;
    vectorUpdateKey?: string;
  };
  engagementOptions?: {
    scrollThreshold?: number;
    elementSelectors?: string[];
    timeThreshold?: number;
  };
}

export class UserPreference {
  private static instance: UserPreference;
  private engagementService: EngagementTracking;

  private isUserAuth: boolean = false;

  private readonly DEFAULT_VECTOR_UPDATE_KEY = "lastVectorUpdate";
  private readonly DEFAULT_ITEMS_KEY = "viewedItems";

  private readonly storageKeys: string[] = [
    this.DEFAULT_VECTOR_UPDATE_KEY,
    this.DEFAULT_ITEMS_KEY,
    "lastClearTime",
  ];

  private userPreferenceUpdater: (
    userId: string,
    userVector: any,
    tags: string[]
  ) => Promise<void> = async () => {
    console.warn("User preference updater not initialized");
  };

  private constructor() {
    this.engagementService = EngagementTracking.getInstance();
  }

  public static getInstance(): UserPreference {
    if (!UserPreference.instance) {
      UserPreference.instance = new UserPreference();
    }
    return UserPreference.instance;
  }

  public initialize(
    isAuthenticated: boolean,
    preferenceUpdater: (
      userId: string,
      userVector: any,
      tags: string[]
    ) => Promise<void>
  ): void {
    this.isUserAuth = isAuthenticated;
    this.userPreferenceUpdater = preferenceUpdater;

    if (!isAuthenticated) {
      this.clearStoredData();
    }
  }

  public registerStorageKey(key: string): void {
    if (!this.storageKeys.includes(key)) {
      this.storageKeys.push(key);
    }
  }

  public setAuthenticationStatus(isAuthenticated: boolean): void {
    this.isUserAuth = isAuthenticated;

    if (!isAuthenticated) {
      this.clearStoredData();
    }
  }

  /**
   * Update user preferences directly
   *
   * @param userId - User identifier
   * @param userVector - User preference vector
   * @param tags - Content tags to update preferences with
   * @param itemId - Content item identifier
   * @param storage - Storage configuration for localStorage
   * @returns Promise resolving to success status
   */
  public async updatePreferences(
    userId: string,
    userVector: any,
    tags: string[],
    itemId: string,
    storage: {
      itemsKey?: string;
      vectorUpdateKey?: string;
    } = {}
  ): Promise<boolean> {
    if (!this.isUserAuth || !userId) {
      return false;
    }

    const itemsKey = storage.itemsKey || this.DEFAULT_ITEMS_KEY;
    const vectorUpdateKey =
      storage.vectorUpdateKey || this.DEFAULT_VECTOR_UPDATE_KEY;

    // Filter out any null/undefined values
    const validTags = tags.filter(Boolean);

    try {
      // Call the userPreferenceUpdater with the filtered tags
      await this.userPreferenceUpdater(userId, userVector, validTags);

      if (itemId) {
        // Update localStorage to track that this item has been processed
        const updatedItems = JSON.parse(localStorage.getItem(itemsKey) || "[]");
        if (!updatedItems.includes(itemId)) {
          localStorage.setItem(vectorUpdateKey, Date.now().toString());
          const newUpdatedItems = [...updatedItems, itemId];
          localStorage.setItem(itemsKey, JSON.stringify(newUpdatedItems));
        }
      }

      return true;
    } catch (error) {
      console.error("Error updating user preferences:", error);
      return false;
    }
  }

  public trackItemEngagement(options: UserPreferenceOptions): void {
    const {
      userId,
      userVector,
      itemId,
      tags = [],
      storage = {},
      engagementOptions = {},
    } = options;

    const itemsKey = storage.itemsKey || this.DEFAULT_ITEMS_KEY;

    this.registerStorageKey(itemsKey);
    this.registerStorageKey(
      storage.vectorUpdateKey || this.DEFAULT_VECTOR_UPDATE_KEY
    );

    if (!this.isUserAuth || !userId || !itemId) {
      return;
    }

    const updatedItems = JSON.parse(localStorage.getItem(itemsKey) || "[]");
    if (updatedItems.includes(itemId)) {
      return;
    }

    const trackingId = `preference_${userId}_${itemId}`;

    this.engagementService.trackEngagement(
      trackingId,
      async () => {
        try {
          console.log(
            `Engagement detected for ${itemId}. Updating preferences with tags.`
          );

          await this.updatePreferences(
            userId,
            userVector,
            tags,
            itemId,
            storage
          );

          console.log(`Preferences updated for ${itemId} and stored.`);
        } catch (error) {
          console.error("Error updating user preferences:", error);
        }
      },
      {
        scrollThreshold: engagementOptions.scrollThreshold || 25,
        elementSelectors: engagementOptions.elementSelectors || [],
        timeThreshold: engagementOptions.timeThreshold || 3000,
        trackOnce: true,
      },
      [userId, itemId, JSON.stringify(tags)]
    );
  }

  public stopTracking(userId: string, itemId: string): void {
    const trackingId = `preference_${userId}_${itemId}`;
    this.engagementService.stopTracking(trackingId);
  }

  private clearStoredData(): void {
    this.storageKeys.forEach((key) => {
      localStorage.removeItem(key);
    });
  }
}
